import { ApolloCache, gql, useApolloClient, useLazyQuery } from "@apollo/client";
import { useEffect } from "react";

import { CourseFragment } from "../../hooks/__generated__/CourseFragment";
import { TrainingInstanceFragment } from "../../hooks/__generated__/TrainingInstanceFragment";
import { COURSE_FRAGMENT_QUERY, TRAINING_INSTANCE_FRAGMENT, useAppState } from "../../hooks/useAppState";
import { ErrorSeverity, useErrorHandler } from "../../hooks/useErrorHandler";
import { CourseDetailsQuery } from "./__generated__/CourseDetailsQuery";
import { InstanceDetailsQuery } from "./__generated__/InstanceDetailsQuery";

export const COURSE_DETAILS_QUERY = gql`
  query CourseDetailsQuery($courseId: Int!) {
    course(courseId: $courseId) {
      id
      courseType
      shortName
      name
      trainingId
      availableResolutions
      fullVersion
      version
      complete
      sections {
        id
        name
        number
        surveyUrl
        modules {
          id
          name
          type
          sectionId
          fullNumber
          duration
          slides {
            id
            moduleId
            name
            number
            fullNumber
            duration
          }
        }
      }
    }
  }
`;

export const INSTANCE_DETAILS_QUERY = gql`
  query InstanceDetailsQuery($courseId: Int!) {
    instance(courseId: $courseId) {
      id
      expirationDateTime
      userId
      progress {
        instanceId
        viewedSeconds
        totalSeconds
        viewedSlides
        totalSlides
        totalQuizzes
        passedQuizzes
        complete
        lastView {
          slideId
          resumeTime
          dateTimeViewed
        }
        moduleProgress {
          id
          name
          type
          fullNumber
          viewedSlides
          totalSlides
          quizScore
        }
      }
    }
  }
`;

function updateCourse(course: CourseFragment, cache: ApolloCache<object>): void {
  // Ensure course has required fields before writing to cache
  if (!course?.trainingId) {
    console.error("Cannot update course cache: course or trainingId is missing", course);
    return;
  }

  try {
    cache.writeQuery({
      id: `Course${course.trainingId}`,
      query: COURSE_FRAGMENT_QUERY,
      data: { course },
    });
  } catch (error) {
    console.error("Failed to write course to cache:", error, course);
  }
}

function updateInstance(instance: TrainingInstanceFragment, cache: ApolloCache<object>): void {
  // Ensure instance has required fields before writing to cache
  if (!instance?.id) {
    console.error("Cannot update instance cache: instance or id is missing", instance);
    return;
  }

  try {
    cache.writeFragment({
      id: `TrainingInstance:${instance.id}`,
      fragment: TRAINING_INSTANCE_FRAGMENT,
      data: instance,
    });
  } catch (error) {
    console.error("Failed to write instance to cache:", error, instance);
  }
}

export function useCourseDetails(courseId: number, instanceId?: string) {
  const { reportError } = useErrorHandler();
  const client = useApolloClient();
  const { setInstance } = useAppState();

  const [runCourseDetailsQuery, { data: courseData, loading: courseLoading, error: courseError }] =
    useLazyQuery<CourseDetailsQuery>(COURSE_DETAILS_QUERY, {
      errorPolicy: "all",
    });

  useEffect(() => {
    if (courseError) {
      reportError(ErrorSeverity.LOW, "Failed to fetch course details", { errors: courseError });
    }
  }, [reportError, courseError]);

  const [runInstanceDetailsQuery, { data: instanceData, loading: instanceLoading, error: instanceError }] =
    useLazyQuery<InstanceDetailsQuery>(INSTANCE_DETAILS_QUERY, {
      errorPolicy: "all",
    });

  useEffect(() => {
    if (instanceError) {
      reportError(ErrorSeverity.LOW, "Failed to fetch instance details", { errors: instanceError });
    }
  }, [reportError, instanceError]);

  const getCourseDetails = async () => {
    const { data: details } = await runCourseDetailsQuery({ variables: { courseId } });
    if (details?.course) {
      updateCourse({ ...details.course, messages: [] }, client.cache);
    }
    if (!instanceId) {
      const { data: instanceDetails } = await runInstanceDetailsQuery({ variables: { courseId } });
      if (instanceDetails?.instance) {
        updateInstance(instanceDetails.instance, client.cache);
        setInstance(instanceDetails.instance.id);
      }
    }
  };

  return {
    getCourseDetails,
    gotDetails: courseData && (instanceId || instanceData),
    loadingDetails: courseLoading || instanceLoading,
  };
}
